import { FacilityOnboardingStep, updateFacilityOnboardingSteps } from "src/api/facility";
import { VerticalSpacing } from "src/designsystem/VerticalSpacing/VerticalSpacing";
import { forwardRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { Heading } from "../FacilityOnboarding.styles";
import { CarouselDot, CarouselWrapper, Task, TaskCheck } from "./Carousel.styles";
import { FacilityAppDownload } from "./FacilityAppDownload";
import { useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { SessionType } from "src/modules/interface";
import { UPDATE_PROFILE } from "src/modules/session";
import { GETTING_STARTED, GettingStartedActionValues } from "../FacilityOnboarding";
import { AdminActionDisabledTooltip } from "src/components/AdminActionDisabledTooltip/AdminActionDisabledTooltip";
import { canAccessTeamMembersRoute, FACILITY_ROLES } from "@src/constants/facility";
import { Carousel as Slider } from "antd";
import { CarouselRef as SliderRef } from "antd/lib/carousel";
import { useOpenUrl } from "@src/mobile/utils/openUrl";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface CarouselProps {
  onboardingSteps?: FacilityOnboardingStep | undefined;
  isAdmin: boolean;
  logGettingStartedEvents: (action: GettingStartedActionValues, subAction?: string) => void;
}

export const Carousel = forwardRef<SliderRef | null, CarouselProps>(
  ({ onboardingSteps, isAdmin, logGettingStartedEvents }, ref) => {
    const dispatch = useDispatch();
    const { profile, user } = useSelector((state: SessionType) => state.session);
    const openUrl = useOpenUrl();
    const canAccessTeamMembers = canAccessTeamMembersRoute({
      isAdmin,
      userAccess: user?.access,
    });
    const { mutateAsync: updateOnboardingSteps } = useMutation({
      mutationFn: ({
        workplaceId,
        steps,
        workplaceUserId,
      }: {
        workplaceId: string;
        steps: Partial<FacilityOnboardingStep>;
        workplaceUserId: string;
      }) => updateFacilityOnboardingSteps(workplaceId, steps, workplaceUserId),
    });
    const [showFacilityAppDownloadModal, setShowFacilityAppDownloadModal] = useState(false);

    const handleCarouselItemClick = async (step: string) => {
      switch (step) {
        case "getMobileApp":
          logGettingStartedEvents(GETTING_STARTED.DOWNLOAD_MOBILE_APP);
          setShowFacilityAppDownloadModal(true);
          break;
        case "addTeamMembers":
          logGettingStartedEvents(GETTING_STARTED.ADD_TEAM_MEMBER);
          if (!(user?.access?.includes(FACILITY_ROLES.DOCUMENTS) && user?.access?.length === 1)) {
            void openUrl("/workplace/users");
          } else {
            void openUrl("/facility/users");
          }
          break;
        case "checkInstructions":
          logGettingStartedEvents(GETTING_STARTED.ADD_CHECK_IN_INSTRUCTIONS);
          void openUrl("/facility/worker-instructions?click_src=onboarding");
          break;
        case "takeProductTour":
          logGettingStartedEvents(GETTING_STARTED.TAKE_PRODUCT_TOUR);

          void openUrl(
            "https://support.clipboardhealth.com/hc/en-us/categories/4943215926167-Facilities"
          );
          if (onboardingSteps?.takeProductTour === false && user?._id) {
            const resp = await updateOnboardingSteps({
              workplaceId: profile.userId,
              steps: {
                takeProductTour: true,
              },
              workplaceUserId: user._id,
            });
            dispatch({
              type: UPDATE_PROFILE,
              data: { ...profile, onboardingSteps: resp },
            });
          }

          break;
        default:
          break;
      }
    };
    const renderStepHeading = (step: string, completed: boolean) => {
      switch (step) {
        case "getMobileApp":
          return completed ? "Mobile app downloaded" : "Download the mobile app";
        case "addTeamMembers":
          return completed ? "Team members added" : "Add team members";
        case "checkInstructions":
          return completed ? "Check-in info added" : "Add check-in information";
        case "takeProductTour":
          return "Need Help?";
        default:
          return "";
      }
    };

    const renderStepDescription = (step: string, completed: boolean) => {
      switch (step) {
        case "getMobileApp":
          return completed
            ? "Use the mobile app to manage your schedule from anywhere."
            : "Get the app so you can manage your schedule from anywhere.";
        case "addTeamMembers":
          return completed
            ? "Click to update your or your team's user information and notification preferences."
            : "Invite teammates to the platform and control their level of access.";
        case "checkInstructions":
          return completed
            ? "Use this page to keep your professional-facing instructions up-to-date."
            : "Share relevant information with workers on how to complete a successful first shift.";
        case "takeProductTour":
          return "Head to our Self-Help Center to get answers to your questions.";
        default:
          return "";
      }
    };

    return (
      <CarouselWrapper>
        <Slider
          dots
          infinite
          speed={500}
          slidesToShow={2}
          swipeToSlide
          ref={ref}
          appendDots={(dots) => <ul style={{ margin: "0px" }}> {dots} </ul>}
          customPaging={() => <CarouselDot />}
        >
          <AdminActionDisabledTooltip disabled={isAdmin}>
            <Task onClick={() => handleCarouselItemClick("getMobileApp")} disabled={isAdmin}>
              {onboardingSteps?.getMobileApp && (
                <TaskCheck>
                  <FaCheck />
                </TaskCheck>
              )}
              <div>
                <Heading>
                  {renderStepHeading("getMobileApp", onboardingSteps?.getMobileApp ?? false)}
                </Heading>
                <VerticalSpacing />
                <div>
                  {renderStepDescription("getMobileApp", onboardingSteps?.getMobileApp ?? false)}
                </div>
              </div>
            </Task>
          </AdminActionDisabledTooltip>
          {canAccessTeamMembers && (
            <AdminActionDisabledTooltip disabled={isAdmin}>
              <Task onClick={() => handleCarouselItemClick("addTeamMembers")} disabled={isAdmin}>
                {onboardingSteps?.addTeamMembers && (
                  <TaskCheck>
                    <FaCheck />
                  </TaskCheck>
                )}
                <div>
                  <Heading>
                    {renderStepHeading("addTeamMembers", onboardingSteps?.addTeamMembers ?? false)}
                  </Heading>
                  <VerticalSpacing />
                  <div>
                    {renderStepDescription(
                      "addTeamMembers",
                      onboardingSteps?.addTeamMembers ?? false
                    )}
                  </div>
                </div>
              </Task>
            </AdminActionDisabledTooltip>
          )}
          <AdminActionDisabledTooltip disabled={isAdmin}>
            <Task onClick={() => handleCarouselItemClick("checkInstructions")} disabled={isAdmin}>
              {onboardingSteps?.checkInstructions && (
                <TaskCheck>
                  <FaCheck />
                </TaskCheck>
              )}
              <div>
                <Heading>
                  {renderStepHeading(
                    "checkInstructions",
                    onboardingSteps?.checkInstructions ?? false
                  )}
                </Heading>
                <VerticalSpacing />
                <div>
                  {renderStepDescription(
                    "checkInstructions",
                    onboardingSteps?.checkInstructions ?? false
                  )}
                </div>
              </div>
            </Task>
          </AdminActionDisabledTooltip>
          <AdminActionDisabledTooltip disabled={isAdmin}>
            <Task onClick={() => handleCarouselItemClick("takeProductTour")} disabled={isAdmin}>
              {onboardingSteps?.takeProductTour && (
                <TaskCheck>
                  <FaCheck />
                </TaskCheck>
              )}
              <div>
                <Heading>
                  {renderStepHeading("takeProductTour", onboardingSteps?.takeProductTour ?? false)}
                </Heading>
                <VerticalSpacing />
                <div>
                  {renderStepDescription(
                    "takeProductTour",
                    onboardingSteps?.takeProductTour ?? false
                  )}
                </div>
              </div>
            </Task>
          </AdminActionDisabledTooltip>
        </Slider>
        {showFacilityAppDownloadModal && (
          <FacilityAppDownload
            visible={showFacilityAppDownloadModal}
            onCancel={() => setShowFacilityAppDownloadModal(false)}
            logGettingStartedEvents={logGettingStartedEvents}
          />
        )}
      </CarouselWrapper>
    );
  }
);
